export { onGenerateLicenseKey }

import { buildLicenseMap } from '../../server/buildLicenseMap.ts'
import { getCustomerOrAbort } from '../../server/auth/getCustomerOrAbort.ts'
import { rateLimitCustomerWrite } from '../../server/auth/rateLimitCustomerWrite.ts'
import { getLicensedDomainsByCustomer, recordDomains } from '../../server/db/queries.ts'
import { parseDomains } from '../../utils/parseDomains.ts'
import { validateDomains } from '../../utils/validateDomains.ts'
import { MAX_DOMAINS_CUSTOMER } from '../../utils/licenseLimits.ts'
import { compareStrings } from '../../utils/compareStrings.ts'
import { RATE_LIMITED_MESSAGE, SUPPORT_EMAIL } from '../../utils/messages.ts'

// Append-only: previously-claimed domains stay; the returned `license` always covers the
// customer's full set so the snippet is complete.
async function onGenerateLicenseKey(
  domainsInput: string,
): Promise<{ inputErrors: { domains: string } } | { license: Record<string, string>; addedDomains: string[] }> {
  const { customerId, env } = getCustomerOrAbort()
  if (!(await rateLimitCustomerWrite(customerId, env))) {
    return { inputErrors: { domains: RATE_LIMITED_MESSAGE } }
  }
  const parsed = parseDomains(domainsInput)
  const validationError = validateDomains(parsed, MAX_DOMAINS_CUSTOMER)
  if (validationError) return { inputErrors: { domains: validationError } }

  const before = new Set(await getLicensedDomainsByCustomer(customerId, env))
  // `recordDomains` is purely additive (no removal, no rewrite — the customer's full license
  // set is the union of every `event__customer_domains.domains_unlocked` array), so this union IS the
  // resulting total; no re-fetch needed.
  const allDomains = [...new Set([...before, ...parsed])].sort(compareStrings)
  // MAX_DOMAINS_CUSTOMER is a per-ACCOUNT cap (the over-cap error below says "account limit"), but
  // `validateDomains` only bounds THIS batch — so without gating the union, repeated submits
  // could grow the set past the cap. Gate on the resulting total before persisting.
  //
  // Known + accepted (don't re-litigate): this read-check-write isn't serialized, so two
  // CONCURRENT submits both read the same `before` and can race the union a domain or two past
  // the cap. Left unguarded on purpose — it's a soft anti-abuse limit, not a hard boundary:
  // `read:check-misuse` flags over-cap accounts, RATE_LIMIT_CUSTOMER_WRITES bounds the rate, and
  // a single customer firing concurrent submits is implausible (the dashboard is opened a few
  // times a year). A transaction/conditional-write to close it isn't worth the complexity.
  if (allDomains.length > MAX_DOMAINS_CUSTOMER) {
    return {
      inputErrors: {
        domains:
          `That would bring you to ${allDomains.length} domains, over the ${MAX_DOMAINS_CUSTOMER}-domain account limit ` +
          `(you have ${before.size}). Email ${SUPPORT_EMAIL} to raise it.`,
      },
    }
  }
  await recordDomains({ customerId, domainsUnlocked: parsed }, env)
  return {
    license: await buildLicenseMap(allDomains, env),
    addedDomains: allDomains.filter((d) => !before.has(d)),
  }
}
